import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef, useButtonGroup, useStyleConfig, omitThemingProps, chakra, useMergeRefs } from '@chakra-ui/react';
import { mergeWith, dataAttr, cx } from '@chakra-ui/utils';
import React__default from 'react';
import useBackwardsCompatibility from '../../utils/useBackwardsCompatibility.js';
import { ButtonContent } from './ButtonContent.js';
import { ButtonSpinner } from './ButtonSpinner.js';
import { useButtonType } from './useButtonType.js';

var _excluded = ["isFloating"],
    _excluded2 = ["isDisabled", "isLoading", "isActive", "isFullWidth", "children", "leftIcon", "rightIcon", "loadingText", "iconSpacing", "type", "spinner", "spinnerPlacement", "className", "as"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
// This maps old variants to new props.

var BACKWARDS_COMPATIBILITY_MAP = {
  variant: {
    blue: {
      colorScheme: 'blue',
      variant: 'solid'
    },
    'blue-secondary': {
      colorScheme: 'blue',
      variant: 'secondary'
    },
    'blue-outline': {
      colorScheme: 'blue',
      variant: 'outline'
    },
    red: {
      colorScheme: 'red',
      variant: 'solid'
    },
    'red-secondary': {
      colorScheme: 'red',
      variant: 'secondary'
    },
    'gray-secondary': {
      colorScheme: 'gray',
      variant: 'secondary'
    }
  },
  sizeVariant: {
    xs: {
      size: 'xs'
    },
    sm: {
      size: 'sm'
    },
    md: {
      size: 'md'
    },
    lg: {
      size: 'lg'
    }
  }
};

/**
 * Button
 *
 * @see Docs https://chakra-ui.com/button
 */
var Button = /*#__PURE__*/forwardRef(function (originalProps, ref) {
  var group = useButtonGroup(); // Extract `isFloating` and only pass it to `useStyleConfig`, not further.
  // `isFloating` triggers a react warning when passed to `chakra.button`
  // because it is not a valid DOM attribute.

  var _useBackwardsCompatib = useBackwardsCompatibility(BACKWARDS_COMPATIBILITY_MAP, originalProps, {
    variant: "The <Button /> variant prop \"".concat(originalProps === null || originalProps === void 0 ? void 0 : originalProps.variant, "\" is deprecated. Please use the appropriate \"colorScheme\" and \"variant\" props."),
    sizeVariant: "The <Button /> sizeVariant prop is deprecated. Use the \"size\" prop with the same value."
  }),
      isFloating = _useBackwardsCompatib.isFloating,
      props = _objectWithoutProperties(_useBackwardsCompatib, _excluded);

  var styles = useStyleConfig('Button', _objectSpread(_objectSpread(_objectSpread({}, group), props), {}, {
    isFloating: isFloating
  }));

  var _omitThemingProps = omitThemingProps(props),
      _omitThemingProps$isD = _omitThemingProps.isDisabled,
      isDisabled = _omitThemingProps$isD === void 0 ? group === null || group === void 0 ? void 0 : group.isDisabled : _omitThemingProps$isD,
      isLoading = _omitThemingProps.isLoading,
      isActive = _omitThemingProps.isActive,
      isFullWidth = _omitThemingProps.isFullWidth,
      children = _omitThemingProps.children,
      leftIcon = _omitThemingProps.leftIcon,
      rightIcon = _omitThemingProps.rightIcon,
      loadingText = _omitThemingProps.loadingText,
      _omitThemingProps$ico = _omitThemingProps.iconSpacing,
      iconSpacing = _omitThemingProps$ico === void 0 ? '0.5rem' : _omitThemingProps$ico,
      type = _omitThemingProps.type,
      spinner = _omitThemingProps.spinner,
      _omitThemingProps$spi = _omitThemingProps.spinnerPlacement,
      spinnerPlacement = _omitThemingProps$spi === void 0 ? 'start' : _omitThemingProps$spi,
      className = _omitThemingProps.className,
      as = _omitThemingProps.as,
      rest = _objectWithoutProperties(_omitThemingProps, _excluded2);
  /**
   * When button is used within ButtonGroup (i.e flushed with sibling buttons),
   * it is important to add a `zIndex` on focus.
   *
   * So let's read the component styles and then add `zIndex` to it.
   */


  var buttonStyles = React__default.useMemo(function () {
    var _focus2;

    // @FIXME: Remove `styles as any`. For some reason, it works in Chakra's
    //   own source code, but not in ours. `styles` is a `SystemStyleObject`,
    //   which should have `_focus`.
    var _focus = mergeWith({}, (_focus2 = styles._focus) !== null && _focus2 !== void 0 ? _focus2 : {}, {
      zIndex: 1
    });

    return _objectSpread(_objectSpread({
      display: 'inline-flex',
      appearance: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      position: 'relative',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      outline: 'none',
      width: isFullWidth ? '100%' : 'auto'
    }, styles), !!group && {
      _focus: _focus
    });
  }, [styles, group, isFullWidth]);

  var _useButtonType = useButtonType(as),
      _ref = _useButtonType.ref,
      defaultType = _useButtonType.type;

  var contentProps = {
    rightIcon: rightIcon,
    leftIcon: leftIcon,
    iconSpacing: iconSpacing,
    children: children
  };
  return /*#__PURE__*/React__default.createElement(chakra.button, _extends({
    disabled: isDisabled || isLoading,
    ref: useMergeRefs(ref, _ref),
    as: as,
    type: type !== null && type !== void 0 ? type : defaultType,
    "data-active": dataAttr(isActive),
    "data-loading": dataAttr(isLoading),
    __css: buttonStyles,
    className: cx('chakra-button', className)
  }, rest), isLoading && spinnerPlacement === 'start' && /*#__PURE__*/React__default.createElement(ButtonSpinner, {
    className: "chakra-button__spinner--start",
    label: loadingText,
    placement: "start"
  }, spinner), isLoading ? loadingText || /*#__PURE__*/React__default.createElement(chakra.span, {
    opacity: 0
  }, /*#__PURE__*/React__default.createElement(ButtonContent, contentProps)) : /*#__PURE__*/React__default.createElement(ButtonContent, contentProps), isLoading && spinnerPlacement === 'end' && /*#__PURE__*/React__default.createElement(ButtonSpinner, {
    className: "chakra-button__spinner--end",
    label: loadingText,
    placement: "end"
  }, spinner));
});
Button.displayName = 'Button';

export { Button };
//# sourceMappingURL=Button.js.map
